import * as React from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { fetchDayOfWeekByWeekday } from '@src/api';
import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { formatDateToYMD } from '@src/utils/dateUtils';

// 0 = Monday … 6 = Sunday (matches the backend's stored `weekday`).
const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const safeDiv = (numerator, denominator) => {
  const num = safeNumber(numerator);
  const denom = safeNumber(denominator);
  return denom ? num / denom : 0;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

function normalizeRows(summaryRows = []) {
  const byWeekday = new Map();

  (summaryRows || []).forEach((row = {}) => {
    const weekday = safeNumber(row.weekday);
    byWeekday.set(weekday, {
      weekday,
      label: row.weekday_name || WEEKDAY_NAMES[weekday] || String(weekday),
      total_spend_sum: safeNumber(row.total_spend_sum),
      total_leads_sum: safeNumber(row.total_leads_sum),
      booked_sum: safeNumber(row.booked_sum),
      scheduled_sum: safeNumber(row.scheduled_sum),
      issues_sum: safeNumber(row.issues_sum),
      demos_sum: safeNumber(row.demos_sum),
      gross_sales_sum: safeNumber(row.gross_sales_sum),
      net_sales_sum: safeNumber(row.net_sales_sum),
      net_amount_sum: safeNumber(row.net_amount_sum),
    });
  });

  // Always render all seven days in order, even with no data for a day.
  return WEEKDAY_NAMES.map(
    (label, weekday) =>
      byWeekday.get(weekday) || {
        weekday,
        label,
        total_spend_sum: 0,
        total_leads_sum: 0,
        booked_sum: 0,
        scheduled_sum: 0,
        issues_sum: 0,
        demos_sum: 0,
        gross_sales_sum: 0,
        net_sales_sum: 0,
        net_amount_sum: 0,
      }
  );
}

function buildTotals(rows = []) {
  return rows.reduce(
    (acc, row = {}) => {
      acc.spend += safeNumber(row.total_spend_sum);
      acc.leads += safeNumber(row.total_leads_sum);
      acc.booked += safeNumber(row.booked_sum);
      acc.issues += safeNumber(row.issues_sum);
      acc.demos += safeNumber(row.demos_sum);
      acc.netSold += safeNumber(row.net_sales_sum);
      acc.netAmount += safeNumber(row.net_amount_sum);
      return acc;
    },
    { spend: 0, leads: 0, booked: 0, issues: 0, demos: 0, netSold: 0, netAmount: 0 }
  );
}

const compactTableSx = {
  '& .MuiTableCell-root': { py: 0.75, px: 1.25, whiteSpace: 'nowrap' },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 700,
    bgcolor: 'background.neutral',
  },
};

function MetricsRow({ label, row, totalLeads }) {
  const spend = safeNumber(row.total_spend_sum);
  const leads = safeNumber(row.total_leads_sum);
  const booked = safeNumber(row.booked_sum);
  const issues = safeNumber(row.issues_sum);
  const demos = safeNumber(row.demos_sum);
  const netSold = safeNumber(row.net_sales_sum);
  const netAmount = safeNumber(row.net_amount_sum);

  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(leads, totalLeads)} /></TableCell>
      <TableCell><DollarFormatter value={spend} /></TableCell>
      <TableCell>{leads}</TableCell>
      <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
      <TableCell>{booked}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(booked, leads)} /></TableCell>
      <TableCell>{issues}</TableCell>
      <TableCell>{demos}</TableCell>
      <TableCell>{netSold}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(netSold, demos)} /></TableCell>
      <TableCell><DollarFormatter value={netAmount} /></TableCell>
      <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
      <TableCell><DollarFormatter value={safeDiv(netAmount, leads)} /></TableCell>
      <TableCell><PercentageFormatter value={safeDiv(spend, netAmount)} /></TableCell>
    </TableRow>
  );
}

export default function DayOfWeekPerformancePage() {
  const defaultRange = React.useMemo(() => getCurrentMonthRange(), []);
  const [startDate, setStartDate] = React.useState(defaultRange.start);
  const [endDate, setEndDate] = React.useState(defaultRange.end);
  const [selectedClientId, setSelectedClientId] = React.useState('');
  const [rawRows, setRawRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientInputChange,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();

  React.useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ...(selectedClientId ? { client_id: selectedClientId } : {}),
        };
        const response = await fetchDayOfWeekByWeekday(payload);
        if (!active) {
          return;
        }
        setRawRows(Array.isArray(response?.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to load day-of-week performance data', err);
        if (active) {
          setError('Failed to load day-of-week performance data.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [startDate, endDate, selectedClientId]);

  const rows = React.useMemo(() => normalizeRows(rawRows), [rawRows]);
  const totals = React.useMemo(() => buildTotals(rows), [rows]);

  const handleDateRangeChange = React.useCallback((ranges) => {
    const nextStart = ranges?.selection?.startDate;
    const nextEnd = ranges?.selection?.endDate;
    if (nextStart instanceof Date && nextEnd instanceof Date) {
      setStartDate(nextStart);
      setEndDate(nextEnd);
    }
  }, []);

  return (
    <Page title="Day of Week Performance" fullWidth isLoading={loading}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5">Day of Week Performance</Typography>
          <Typography variant="body2" color="text.secondary">
            Spend, lead funnel and revenue collapsed into the seven days of the
            week across the selected window. Use it to spot which days convert
            best; move the date range to see how the pattern shifts over time.
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 1.25, md: 1.5 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
                style={{ width: '100%' }}
              />
            </Box>
            <Autocomplete
              sx={{ minWidth: 260 }}
              options={clientOptions}
              loading={clientsLoading}
              value={findClientOptionByValue(selectedClientId)}
              onInputChange={handleClientInputChange}
              onChange={(_event, option) => setSelectedClientId(option ? String(option.value) : '')}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              getOptionLabel={(option) => option?.label || ''}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="All clients" />
              )}
            />
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Card>
          <CardHeader
            title="Performance by Day of Week"
            subheader="Aggregated across the selected date range and client (Monday → Sunday)."
          />
          <CardContent>
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={compactTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell>Lead Mix</TableCell>
                    <TableCell>Spend</TableCell>
                    <TableCell>Leads</TableCell>
                    <TableCell>CPL</TableCell>
                    <TableCell>Booked</TableCell>
                    <TableCell>Set Rate</TableCell>
                    <TableCell>Issues</TableCell>
                    <TableCell>Demos</TableCell>
                    <TableCell>Net Sold</TableCell>
                    <TableCell>Close Rate</TableCell>
                    <TableCell>Net Amount</TableCell>
                    <TableCell>Avg Deal</TableCell>
                    <TableCell>RPL</TableCell>
                    <TableCell>Cost of Marketing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {totals.leads || totals.spend ? (
                    <>
                      {rows.map((row) => (
                        <MetricsRow
                          key={row.weekday}
                          label={row.label}
                          row={row}
                          totalLeads={totals.leads}
                        />
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell />
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={totals.spend} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.leads}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.spend, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.booked}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.booked, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.issues}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.demos}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.netSold}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.netSold, totals.demos)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={totals.netAmount} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.netAmount, totals.netSold)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.netAmount, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.spend, totals.netAmount)} />
                        </TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={15} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {loading ? 'Loading…' : 'No data for the selected range.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>
    </Page>
  );
}
